import React, { useState } from 'react';
import { Tv, Search, Loader, Wifi, X, Check } from 'lucide-react';

const API_BASE_URL = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
const buildApiUrl = (path) => (API_BASE_URL ? `${API_BASE_URL}${path}` : path);

const TVDiscovery = ({ onTVSelected }) => {
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveredTVs, setDiscoveredTVs] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTV, setSelectedTV] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [manualIP, setManualIP] = useState('');
  const [manualMAC, setManualMAC] = useState('');

  // Start TV discovery
  const discoverTVs = async () => {
    setIsDiscovering(true);
    setError(null);
    
    try {
      const response = await fetch(buildApiUrl('/api/discover-tvs'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDiscoveredTVs(data.tvs);
        if (data.tvs.length === 0) {
          setError('No TVs found on the network');
        }
      } else {
        setError(data.message || 'Failed to discover TVs');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Discovery error:', err);
    } finally {
      setIsDiscovering(false);
    }
  };

  // Connect to a TV
  const connectToTV = async (ip, mac) => {
    setIsConnecting(true);
    setError(null);
    
    try {
      const response = await fetch(buildApiUrl('/api/connect-tv'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip, mac })
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (onTVSelected) {
          onTVSelected({ ip, mac });
        }
      } else {
        setError(data.message || 'Failed to connect to TV');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Connection error:', err);
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle TV selection
  const handleTVSelect = (tv) => {
    setSelectedTV(tv);
  };

  // Handle connect button click
  const handleConnect = () => {
    if (selectedTV) {
      connectToTV(selectedTV.ip, selectedTV.mac);
    }
  };

  // Handle manual IP connect
  const handleManualConnect = () => {
    if (manualIP) {
      connectToTV(manualIP, manualMAC || null);
    } else {
      setError('Please enter a valid IP address');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-6">Find Your TV</h2>
      
      {/* Discovery section */}
      <div className="mb-8">
        <div className="flex justify-center mb-4">
          <button 
            className="control-button navigation-button flex items-center justify-center"
            onClick={discoverTVs}
            disabled={isDiscovering}
          >
            {isDiscovering ? (
              <Loader className="animate-spin" size={32} />
            ) : (
              <>
                <Search size={32} />
                <span className="ml-2">Scan for TVs</span>
              </>
            )}
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* TV list */}
        {discoveredTVs.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-3">Found TVs:</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {discoveredTVs.map((tv, index) => (
                <div 
                  key={index}
                  className={`p-4 border rounded-lg cursor-pointer flex items-center ${
                    selectedTV && selectedTV.ip === tv.ip 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                  onClick={() => handleTVSelect(tv)}
                >
                  <Tv size={24} className="text-gray-600 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium">{tv.name}</div>
                    <div className="text-sm text-gray-500">IP: {tv.ip}</div>
                  </div>
                  {selectedTV && selectedTV.ip === tv.ip && (
                    <Check size={24} className="text-blue-500" />
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 flex justify-center">
              <button 
                className="control-button power-button flex items-center justify-center"
                onClick={handleConnect}
                disabled={!selectedTV || isConnecting}
              >
                {isConnecting ? (
                  <Loader className="animate-spin" size={32} />
                ) : (
                  <>
                    <Wifi size={32} />
                    <span className="ml-2">Connect to Selected TV</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Manual connection section */}
      <div className="border-t pt-6">
        <h3 className="text-xl font-semibold mb-3">Manual Connection:</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 text-lg mb-2" htmlFor="ip-address">
              TV IP Address (required):
            </label>
            <input
              id="ip-address"
              type="text"
              className="shadow appearance-none border rounded w-full py-3 px-4 text-xl text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="192.168.1.100"
              value={manualIP}
              onChange={(e) => setManualIP(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-lg mb-2" htmlFor="mac-address">
              TV MAC Address (optional):
            </label>
            <input
              id="mac-address"
              type="text"
              className="shadow appearance-none border rounded w-full py-3 px-4 text-xl text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="00:11:22:33:44:55"
              value={manualMAC}
              onChange={(e) => setManualMAC(e.target.value)}
            />
          </div>
          
          <div className="flex justify-center mt-4">
            <button 
              className="control-button volume-button flex items-center justify-center"
              onClick={handleManualConnect}
              disabled={!manualIP || isConnecting}
            >
              {isConnecting ? (
                <Loader className="animate-spin" size={32} />
              ) : (
                <>
                  <Wifi size={32} />
                  <span className="ml-2">Connect Manually</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TVDiscovery;
